import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async'; // Added for SEO
import api from "../../api/axios";

const SearchContent = () => {
  const { search } = useParams(); 
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/products/search?q=${search}`);
        if (res.data.success) {
          setFilteredProducts(res.data.data);
        }
      } catch (err) {
        console.error("Search error", err);
      } finally {
        setLoading(false);
      }
    };
  
    if (search) fetchResults();
  }, [search]);

  const ProductCard = ({ product }) => {
    const price = parseFloat(product.price);
    const oldPrice = product.old_price ? parseFloat(product.old_price) : null;
    const discount = (oldPrice && oldPrice > price) 
      ? Math.round(((oldPrice - price) / oldPrice) * 100) 
      : 0;

    const imageSrc = product.main_image.startsWith('http') 
      ? product.main_image 
      : product.main_image.startsWith('/storage') 
        ? `${BACKEND_BASE}${product.main_image}` 
        : `${BACKEND_BASE}/storage/${product.main_image}`;

    return (
      <Link to={`/product/${product.slug}`} className="group flex flex-col bg-white p-4 transition-all duration-300 hover:shadow-2xl border border-gray-50 rounded-2xl">
        <div className="relative mb-4 h-40 w-full overflow-hidden sm:h-56">
          {discount > 0 && (
            <div className="absolute top-0 left-0 z-10 bg-[#ffaa00] px-3 py-1 text-[10px] font-black text-white uppercase tracking-wider rounded-br-lg">
              -{discount}%
            </div>
          )}
          {/* SEO: Added alt text using product title */}
          <img 
            src={imageSrc} 
            alt={`${product.brand} ${product.title}`} 
            className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-110" 
            loading="lazy"
          />
        </div>
        <div className="flex flex-col text-center mt-auto">
          <span className="text-[10px] font-bold text-[#8A8A89] uppercase tracking-widest mb-1">
            {product.brand || 'Electrotitan'}
          </span>
          <h3 className="font-sans text-xs font-bold text-gray-800 line-clamp-2 min-h-[32px] mb-2 uppercase tracking-tight group-hover:text-[#ffaa00] transition-colors">
            {product.title}
          </h3>
          <div className="flex flex-col items-center gap-1">
            <span className="text-lg font-black text-[#ffaa00]">
              {product.price} <span className="text-[10px]">DH</span>
            </span>
            {product.old_price && (
              <span className="text-xs text-[#8A8A89] line-through font-bold">
                {product.old_price} DH
              </span>
            )}
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
      {/* --- SEO START --- */}
      <Helmet>
        <title>{`Résultats pour "${search}" | Electro Titan`}</title>
        {/* Important: Don't index search results to avoid duplicate content issues */}
        <meta name="robots" content="noindex, follow" />
        <meta key="og:title" property="og:title" content={`Recherche : ${search} sur Electro Titan`} />
        <meta key="og:description" property="og:description" content={`Découvrez nos produits correspondant à "${search}" au meilleur prix au Maroc.`} />
      </Helmet>
      {/* --- SEO END --- */}

      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <span className="text-[#8A8A89] font-bold text-xs uppercase tracking-widest">Résultats de recherche pour :</span>
          <h1 className="text-3xl font-black uppercase tracking-tighter">{search}</h1>
          <p className="text-sm font-bold text-[#8A8A89] mt-1">
            {loading ? "Chargement..." : `${filteredProducts.length} produits trouvés`}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-[#ffaa00]" size={40} />
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="flex-1">
              <div className="grid grid-cols-2 gap-x-3 gap-y-8 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-20 border-2 border-dashed border-gray-100 rounded-[2rem]">
            <p className="text-3xl mb-4">🔍</p>
            <h2 className="text-xl font-bold text-gray-900">Aucun résultat trouvé</h2>
            <p className="text-[#8A8A89] mb-8">Désolé, nous n'avons trouvé aucun produit correspondant à votre recherche.</p>
            <Link to="/" className="px-8 py-3 bg-[#ffaa00] text-white font-black uppercase tracking-widest rounded-xl text-xs">Retour à l'accueil</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchContent;